import React, {
  KeyboardEventHandler,
  KeyboardEvent,
  ReactNode,
  useState,
  useEffect
} from 'react'
import Skeleton from 'react-loading-skeleton'

import classnames from 'classnames'
import { v4 as uuidv4 } from 'uuid'

import { normalizeHtmlId } from 'src/shared/js/normalizeUtils'

import Image from 'shared/components/common/Image'

import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'
import { useCart } from 'public/components/online_ordering/CartContext'
import { t } from 'src/translations'

const CheckoutSection = ({
  title,
  icon,
  collapsible = false,
  defaultExpanded = false,
  children
}: {
  title?: string
  icon?: ReactNode
  collapsible?: boolean
  /** When true, section starts expanded and auto-expands when it becomes true (e.g. when validation problems load). */
  defaultExpanded?: boolean
  children: ReactNode
}) => {
  const { cart } = useCart()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const id = React.useMemo(() => normalizeHtmlId(uuidv4()), [])

  useEffect(() => {
    if (defaultExpanded) {
      setExpanded(true)
    }
  }, [defaultExpanded])

  const handleKeyDown = React.useCallback<KeyboardEventHandler<HTMLDivElement>>(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case ' ':
        case 'Enter':
          e.stopPropagation()
          e.preventDefault()
          setExpanded(!expanded)
          break
      }
    },
    [expanded]
  )

  if (!cart) {
    return (
      <section className='checkoutSectionSideBySide'>
        <Skeleton width='100%' height='500px' />
      </section>
    )
  }

  if (collapsible && title) {
    return (
      <section className='checkoutSectionSideBySide'>
        <div className='separator top' />
        <h2
          id={`accordionheader${id}`}
          role='button'
          aria-controls={`accordionpanel${id}`}
          aria-expanded={expanded}
          className={classnames(
            'checkoutSectionHeader',
            'flexHeader',
            'collapsible'
          )}
          onClick={() => setExpanded(!expanded)}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        >
          <span className='headerLeft'>
            {icon}
            {title}
          </span>
          <Image
            alt={
              expanded
                ? t('checkout-section.collapse')
                : t('checkout-section.expand')
            }
            className={classnames(
              'controlIcon',
              expanded ? 'rotatedUp' : 'rotatedDown'
            )}
            src='icons/chevron-right-gray.svg'
          />
        </h2>
        {expanded ? (
          <div className='separator middle' />
        ) : (
          <div className='separator bottom' />
        )}
        <div
          id={`accordionpanel${id}`}
          aria-labelledby={`accordionheader${id}`}
          role='region'
        >
          <AnimatedSection expanded={expanded} className='animatedSection'>
            <div className='animatedSectionChildrenWrapper'>{children}</div>
          </AnimatedSection>
        </div>
        {expanded && <div className='separator bottom' />}
      </section>
    )
  }

  return (
    <section className='checkoutSectionSideBySide'>
      {title && (
        <h2 className={classnames('checkoutSectionHeader')}>{title}</h2>
      )}
      {children}
    </section>
  )
}

export default CheckoutSection
